import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ASSETS, CONTACT } from "../lib/brand";

const PRESS = [
  "Gazeta do Povo",
  "Bem Paraná",
  "TV Paraná",
  "Rádio Massa",
  "Revista TOP",
  "BNI Brasil",
];

const STATS = [
  { n: "+4", label: "Anos moldando a moda circular no Sul" },
  { n: "+2.5k", label: "Peças com nova história" },
  { n: "+30", label: "Entrevistas e aparições na mídia" },
  { n: "1", label: "Membro ativa BNI — Curitiba" },
];

export default function Sobre() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <section
      id="sobre"
      ref={ref}
      data-testid="sobre-section"
      className="relative bg-cream-100 py-24 sm:py-40"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 grid grid-cols-12 gap-6 lg:gap-16">
        {/* Image with video invite */}
        <div className="col-span-12 lg:col-span-5 relative">
          <div className="relative overflow-hidden aspect-[4/5] bg-cream-200 group">
            <motion.img
              src={ASSETS.hero}
              alt="Herida — retrato"
              initial={{ scale: 1.15 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <motion.div
              initial={{ scaleY: 1 }}
              animate={inView ? { scaleY: 0 } : {}}
              transition={{ duration: 1.3, ease: [0.76, 0, 0.24, 1] }}
              style={{ transformOrigin: "bottom" }}
              className="absolute inset-0 bg-ink-800 z-10"
            />

            {/* Sophisticated video invite — floating card */}
            <motion.a
              href={CONTACT.reel}
              target="_blank"
              rel="noreferrer"
              aria-label="Assistir vídeo de apresentação da Herida"
              data-testid="sobre-video-cta"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute z-20 left-4 sm:left-6 bottom-4 sm:bottom-6 right-4 sm:right-6"
            >
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-4 sm:gap-5 bg-cream/95 backdrop-blur-md px-4 sm:px-5 py-4 sm:py-5 border border-cream"
              >
                <span className="relative flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-brick-500 flex items-center justify-center text-cream overflow-hidden">
                  <span className="absolute inset-0 bg-cream/20 scale-0 group-hover:scale-100 transition-transform duration-700 ease-out rounded-full" />
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    className="relative"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M8 5.14v13.72a1 1 0 001.55.83l10.03-6.86a1 1 0 000-1.66L9.55 4.31A1 1 0 008 5.14z" />
                  </svg>
                </span>
                <span className="flex flex-col text-left min-w-0">
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-brick-500">
                    ● Reel · 60&#8202;s
                  </span>
                  <span
                    className="mt-1 font-serif italic text-ink-800 leading-none truncate"
                    style={{ fontSize: "clamp(1.1rem, 1.4vw, 1.5rem)" }}
                  >
                    Me conheça em vídeo
                  </span>
                </span>
                <span className="ml-auto font-serif italic text-2xl text-brick-500 group-hover:translate-x-1 transition-transform duration-500">
                  →
                </span>
              </motion.div>
            </motion.a>
          </div>
          <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60">
            <span>A curadora</span>
            <span>Fundadora — desde 2020</span>
          </div>
        </div>

        {/* Text */}
        <div className="col-span-12 lg:col-span-7">
          <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60 mb-8">
            <span className="w-8 h-px bg-brick-500" />
            <span>Sobre a Herida</span>
          </div>

          <div className="overflow-hidden">
            <motion.h2
              initial={{ y: "110%" }}
              animate={inView ? { y: 0 } : {}}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif leading-[0.9] tracking-tight text-ink-800"
              style={{ fontSize: "clamp(3rem, 7vw, 7.5rem)" }}
            >
              Uma mulher, <br />
              <span className="italic-serif text-brick-500">um propósito</span>.
            </motion.h2>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 font-serif text-2xl sm:text-3xl leading-snug text-ink-800 max-w-2xl"
          >
            Herida é referência em moda circular no Brasil.
            Fala nas rádios, escreve na imprensa e movimenta o mercado
            <span className="italic-serif text-brick-500"> second hand </span>
            com garra, estilo e uma visão que já virou movimento.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mt-8 font-sans text-base sm:text-lg leading-relaxed text-ink-800/70 max-w-xl"
          >
            Membro ativa do BNI — rede de negócios que reúne empresárias
            e empresários de alta performance — Herida transformou o
            desapego em plataforma de autoridade, negócio e propósito.
          </motion.p>

          {/* Stats */}
          <div className="mt-14 grid grid-cols-2 gap-y-10 gap-x-6 max-w-2xl">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.7 + i * 0.1, duration: 0.7 }}
                className="border-t border-ink-800/20 pt-4"
                data-testid={`stat-${i}`}
              >
                <div
                  className="font-serif text-brick-500"
                  style={{ fontSize: "clamp(2.5rem, 4vw, 3.75rem)", lineHeight: 1 }}
                >
                  {s.n}
                </div>
                <div className="mt-2 font-sans text-xs sm:text-sm text-ink-800/70 max-w-[16rem]">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Press */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-16 pt-8 border-t border-ink-800/20"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60 mb-6">
              Na mídia
            </div>
            <div className="flex flex-wrap gap-x-10 gap-y-4">
              {PRESS.map((p) => (
                <span
                  key={p}
                  className="font-serif italic text-lg sm:text-xl text-ink-800/70 hover:text-brick-500 transition-colors duration-500"
                >
                  {p}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
